/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '@services/api';
import { loadList } from '@/lib/loadResult';

/**
 * The achievement notifications behind the header bell, fetched once.
 *
 * Duplicate-logic finding #27: `Header` and `NotificationsPanel` each ran their
 * own `Promise.all` over GET /badges/me and GET /certificates/me, and each
 * carried a byte-identical copy of the dismissed-set reader. Loading a page and
 * then clicking the bell was four requests for two lists — the header had
 * already fetched both. Both localStorage helpers were duplicated with them, so
 * a change to how dismissals are stored had to be made twice.
 *
 * This hook owns all three things: the two requests, the dismissed set under
 * `lms_notifs_dismissed_${userId}`, and the unread count against
 * `lms_notifs_seen_${userId}`. `Header` calls it once and hands the result to
 * the panel, so the count in the badge is derived from the same list the panel
 * renders and the bell costs no further requests to open.
 *
 * The key names and their JSON-array format are load-bearing: they are already
 * written in every existing user's browser, so changing either would silently
 * reset everyone's dismissed list.
 */

/** Alerts have their own on-page card; the bell is achievements only. */
export interface NotifItem {
  id: string;
  type: 'badge' | 'cert';
  title: string;
  subtitle: string;
  /** Raw `issued_at`; the relative label is formatted at render time. */
  issuedAt: string;
  rawDate: Date;
}

export interface NotificationsView {
  /** Undismissed achievements, newest first. */
  items: NotifItem[];
  loading: boolean;
  /**
   * True when either request failed. "You're all caught up" is a claim about
   * the account, so the panel may only say it when both requests answered
   * (survey 03-silent-failure #15).
   */
  failed: boolean;
  unreadCount: number;
  dismiss: (id: string) => void;
  clearAll: () => void;
  /** Stamp "seen" and zero the badge — what opening the bell means. */
  markSeen: () => void;
  refetch: () => void;
}

const dismissedKey = (userId: string) => `lms_notifs_dismissed_${userId}`;
const seenKey = (userId: string) => `lms_notifs_seen_${userId}`;

function loadDismissed(userId: string): Set<string> {
  try {
    const raw = localStorage.getItem(dismissedKey(userId));
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function saveDismissed(userId: string, ids: Set<string>): void {
  try {
    localStorage.setItem(dismissedKey(userId), JSON.stringify([...ids]));
  } catch {
    // Storage can be full or blocked; the dismissal still applies this session.
  }
}

/*
 * The two response row shapes, named rather than `Record<string, string>`.
 * The header's copy had a typed `Achievement { id, issued_at }` and the panel's
 * had the index signature; merging them must not lose the stronger of the two,
 * or a field rename on either endpoint fails silently instead of at compile
 * time (raised by the PR review bot). The optional fields are the ones the
 * mappers below already guard with `??`.
 */
interface BadgeRow {
  id: string;
  issued_at: string;
  name?: string;
  description?: string;
}

interface CertificateRow {
  id: string;
  issued_at: string;
  course_title?: string;
}

function toBadgeItem(b: BadgeRow): NotifItem {
  return {
    id: `badge-${b.id}`,
    type: 'badge',
    title: b.name ?? 'Badge earned',
    subtitle: b.description ? b.description.slice(0, 70) : '',
    issuedAt: b.issued_at,
    rawDate: new Date(b.issued_at),
  };
}

function toCertItem(c: CertificateRow): NotifItem {
  return {
    id: `cert-${c.id}`,
    type: 'cert',
    title: 'Certificate of Completion',
    subtitle: c.course_title ?? '',
    issuedAt: c.issued_at,
    rawDate: new Date(c.issued_at),
  };
}

/** The later of a stored stamp and an in-session one; either may be absent. */
function laterOf(stored: string, inSession: string | null): string {
  if (!inSession) return stored;
  return new Date(inSession) > new Date(stored) ? inSession : stored;
}

/** Unread = not dismissed and issued after the last time the bell was opened. */
function countUnread(items: NotifItem[], dismissed: Set<string>, lastSeen: string): number {
  return items.filter(
    (item) => !dismissed.has(item.id) && new Date(item.issuedAt) > new Date(lastSeen)
  ).length;
}

export function useNotifications(userId: string | undefined): NotificationsView {
  const [allItems, setAllItems] = useState<NotifItem[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  /*
   * Which fetch is allowed to write. Consolidating two components' fetches into
   * one hook introduced a second trigger for a single piece of state — the
   * `userId` effect and the panel's `refetch` — so two runs can now overlap and
   * resolve in either order. Without this token the earlier one wins whenever
   * it finishes last: a stale "Could not load" would survive a successful
   * retry, and a fetch outstanding across a logout or a user switch would
   * repaint the previous identity's badges over the cleared state. Each run
   * claims a number on entry and drops its results if a newer run has claimed
   * one since. (Raised by the PR review bot on this file.)
   */
  const generation = useRef(0);

  /*
   * The last time the bell was opened *in this session*. The generation token
   * above orders fetch against fetch, but not fetch against `markSeen`: the
   * bell renders before the mount fetch resolves, so a user who opens it inside
   * that window gets the count zeroed and the stamp written, and then the
   * resolving fetch counts against the `lastSeen` it snapshotted BEFORE the
   * click and repaints a non-zero badge over a panel that is open and read.
   * Taking the later of the two instants fixes it without another round trip.
   * (Raised by the PR review bot; the same race existed in the header's own
   * effect before the two copies were merged.)
   */
  const seenAt = useRef<{ userId: string; at: string } | null>(null);

  const fetchNotifications = useCallback(async () => {
    const mine = ++generation.current;
    const isCurrent = () => generation.current === mine;

    if (!userId) {
      /*
       * No user, no notifications — and that means clearing what a previous
       * user left behind, not just stopping. Both former copies only stopped
       * (`if (!userId) return`), which was harmless while each component owned
       * its own state and unmounted on logout; a hook whose state outlives the
       * identity change would otherwise keep showing the last user's items and
       * count. Flagged by Aikido's PR review on this file.
       */
      setAllItems([]);
      setDismissedIds(new Set());
      setUnreadCount(0);
      setFailed(false);
      setLoading(false);
      return;
    }

    // Snapshot both localStorage reads before the await, exactly as the two
    // former copies did: the count is against the bell's state at fetch time.
    const dismissed = loadDismissed(userId);
    const lastSeen = localStorage.getItem(seenKey(userId)) ?? '1970-01-01T00:00:00Z';
    setDismissedIds(dismissed);
    setLoading(true);

    const [badgesRes, certsRes] = await Promise.all([
      loadList<BadgeRow>('hooks/useNotifications badges', api.get(`/badges/me?userId=${userId}`)),
      loadList<CertificateRow>(
        'hooks/useNotifications certificates',
        api.get(`/certificates/me?user_id=${userId}`)
      ),
    ]);

    // A newer run has started since; its answer is the one that counts.
    if (!isCurrent()) return;

    const items = [
      ...(badgesRes.ok ? badgesRes.data : []).map(toBadgeItem),
      ...(certsRes.ok ? certsRes.data : []).map(toCertItem),
    ].sort((a, b) => b.rawDate.getTime() - a.rawDate.getTime());

    setAllItems(items);
    // Tagged with the identity, so one user's "seen" instant is never applied
    // to another's count after a switch.
    const seenThisSession = seenAt.current?.userId === userId ? seenAt.current.at : null;
    setUnreadCount(countUnread(items, dismissed, laterOf(lastSeen, seenThisSession)));
    setFailed(!badgesRes.ok || !certsRes.ok);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void fetchNotifications();
  }, [fetchNotifications]);

  const dismiss = useCallback(
    (id: string) => {
      if (!userId) return;
      setDismissedIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        saveDismissed(userId, next);
        return next;
      });
    },
    [userId]
  );

  const clearAll = useCallback(() => {
    if (!userId) return;
    const allIds = new Set(allItems.map((i) => i.id));
    saveDismissed(userId, allIds);
    setDismissedIds(allIds);
  }, [userId, allItems]);

  const markSeen = useCallback(() => {
    if (!userId) return;
    const now = new Date().toISOString();
    localStorage.setItem(seenKey(userId), now);
    // Also remembered in a ref, so a fetch still in flight cannot count
    // against the pre-click stamp it snapshotted. See `seenAt` above.
    seenAt.current = { userId, at: now };
    setUnreadCount(0);
  }, [userId]);

  const items = useMemo(
    () => allItems.filter((item) => !dismissedIds.has(item.id)),
    [allItems, dismissedIds]
  );

  return {
    items,
    loading,
    failed,
    unreadCount,
    dismiss,
    clearAll,
    markSeen,
    refetch: () => void fetchNotifications(),
  };
}
